import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';

// FASE QUIZZES · PASO 4E — Pantalla de resultado final.
// Fuente de verdad: quiz_assignment.final_score (calculado por el backend
// en POST /progress al completar la última actividad). El frontend sólo
// presenta el valor recibido; nunca recalcula.

const MESSAGES = {
  10: { title: '¡EXTRAORDINARIO!', emoji: '🏆', line: '¡Demostraste un gran dominio!' },
  9:  { title: '¡EXCELENTE!',      emoji: '🌟', line: '¡Has demostrado un gran dominio de este tema!' },
  8:  { title: '¡MUY BUEN TRABAJO!',emoji: '🎉', line: '¡Cada reto te ayuda a seguir mejorando!' },
  7:  { title: '¡BUEN TRABAJO!',   emoji: '💪', line: '¡Vas por buen camino! Sigue practicando y cada vez dominarás más.' },
};

function Confetti() {
  // Confeti CSS puro — 800ms, sin librerías extra.
  return (
    <div data-testid="qr-confetti" aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden z-40">
      {Array.from({ length: 60 }).map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 200;
        const dur = 700 + Math.random() * 400;
        const rot = Math.random() * 360;
        const colors = ['#f59e0b','#ef4444','#10b981','#3b82f6','#8b5cf6','#ec4899'];
        const c = colors[i % colors.length];
        return (
          <span key={i} style={{
            position: 'absolute', top: '-6px', left: `${left}%`, width: 8, height: 12,
            background: c, transform: `rotate(${rot}deg)`,
            animation: `cf-fall ${dur}ms ${delay}ms ease-out forwards`,
          }}/>
        );
      })}
      <style>{`@keyframes cf-fall { to { transform: translateY(105vh) rotate(720deg); opacity: 0.85 } }`}</style>
    </div>
  );
}

export default function QuizResult({ assignment, onContinue }) {
  const [showConfetti, setShowConfetti] = useState(false);
  const grade = Math.max(7, Math.min(10, Number(assignment?.final_score || 7)));
  const msg = MESSAGES[grade] || MESSAGES[7];

  useEffect(() => {
    if (grade === 10) { setShowConfetti(true); const t = setTimeout(() => setShowConfetti(false), 900); return () => clearTimeout(t); }
  }, [grade]);

  if (!assignment) {
    return <div className="min-h-[calc(100vh-4rem)] grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin"/></div>;
  }

  return (
    <div data-testid="quiz-result" className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-emerald-50 via-white to-indigo-50 grid place-items-center p-6">
      {showConfetti && <Confetti/>}
      <div className="max-w-lg w-full text-center rounded-3xl bg-white border border-slate-200 shadow-xl p-8 sm:p-12 relative overflow-hidden">
        <div className="text-7xl sm:text-8xl mb-4" style={{ animation: 'qr-pop 700ms ease-out' }}>{msg.emoji}</div>
        <div data-testid="quiz-result-title" className="font-display font-black text-2xl sm:text-3xl text-slate-900 tracking-tight">{msg.title}</div>

        <div className="my-6 grid place-items-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white px-6 py-2 shadow-lg">
            <span className="text-3xl">⭐</span>
            <span data-testid="quiz-result-grade" className="text-5xl sm:text-6xl font-display font-black">{grade}</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-2">Calificación final</div>
        </div>

        <div data-testid="quiz-result-message" className="text-slate-600 text-base sm:text-lg">{msg.line}</div>

        <div className="mt-8">
          <Button data-testid="quiz-result-continue" onClick={onContinue} className="h-12 px-10 text-base bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl">
            <ArrowLeft className="w-4 h-4 mr-1"/> Continuar
          </Button>
        </div>
        <style>{`@keyframes qr-pop { 0% { transform: scale(0.4); opacity: 0 } 60% { transform: scale(1.15); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }`}</style>
      </div>
    </div>
  );
}
